import { Link, useLocation } from "react-router";
import { UsersIcon } from "./icons/UsersIcon";
import { CacheIcon } from "./icons/CacheIcon";
import { HealthIcon } from "./icons/HealthIcon";
import { EconomicsIcon } from "./icons/EconomicsIcon";
import { ModulesIcon } from "./icons/ModulesIcon";
import { SchrijvenIcon } from "./icons/SchrijvenIcon";
import { ReviewQueueIcon } from "./icons/ReviewQueueIcon";
import { useT } from "../../lib/use-t";

type IconKey =
  | "users"
  | "modules"
  | "schrijven"
  | "review-queue"
  | "cache"
  | "health"
  | "economics";

type AdminNavGroup = "people" | "system";

type AdminNavItem = {
  labelKey: string;
  href: string;
  iconKey: IconKey;
  group: AdminNavGroup;
};

const ADMIN_NAV: AdminNavItem[] = [
  { labelKey: "admin.sidebar.nav.users", href: "/admin", iconKey: "users", group: "people" },
  { labelKey: "admin.sidebar.nav.modules", href: "/admin/modules", iconKey: "modules", group: "system" },
  { labelKey: "admin.sidebar.nav.schrijvenVariants", href: "/admin/schrijven/variants", iconKey: "schrijven", group: "system" },
  { labelKey: "admin.sidebar.nav.reviewQueue", href: "/admin/schrijven/review-queue", iconKey: "review-queue", group: "system" },
  { labelKey: "admin.sidebar.nav.cache", href: "/admin/cache", iconKey: "cache", group: "system" },
  { labelKey: "admin.sidebar.nav.health", href: "/admin/health", iconKey: "health", group: "system" },
  { labelKey: "admin.sidebar.nav.economics", href: "/admin/health/economics", iconKey: "economics", group: "system" },
];

const GROUP_LABEL_KEY: Record<AdminNavGroup, string> = {
  people: "admin.sidebar.group.people",
  system: "admin.sidebar.group.system",
};

const ICONS = {
  users: UsersIcon,
  modules: ModulesIcon,
  schrijven: SchrijvenIcon,
  "review-queue": ReviewQueueIcon,
  cache: CacheIcon,
  health: HealthIcon,
  economics: EconomicsIcon,
} as const;

export function AdminSidebar() {
  const { pathname } = useLocation();
  const t = useT();

  // Longest-match wins so /admin/health/economics highlights only the
  // economics entry, not the parent /admin/health Status.
  const matching = ADMIN_NAV.map((i) => i.href).filter(
    (h) => pathname === h || pathname.startsWith(h + "/"),
  );
  const activeHref = matching.reduce(
    (best, h) => (h.length > best.length ? h : best),
    "",
  );

  function navLink(item: AdminNavItem) {
    const isActive = item.href === activeHref;
    const Icon = ICONS[item.iconKey];
    return (
      <Link
        to={item.href}
        aria-current={isActive ? "page" : undefined}
        className={
          "flex items-center gap-2.5 rounded-[var(--radius)] px-3 min-h-11 py-2 text-sm font-medium whitespace-nowrap transition-colors " +
          (isActive
            ? "bg-[var(--accent)] text-white"
            : "text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--panel-soft)]")
        }
      >
        <Icon className="h-[17px] w-[17px]" />
        <span>{t(item.labelKey)}</span>
      </Link>
    );
  }

  // Group order is preserved by iterating the static ADMIN_NAV; the eyebrow is
  // emitted at the first item of each group (desktop only — the mobile strip is
  // a single horizontal scroll row without section breaks).
  let lastGroup: AdminNavGroup | null = null;

  return (
    <nav
      aria-label={t("admin.sidebar.ariaLabel")}
      className="md:w-[200px] md:shrink-0 border-b border-[var(--border)] md:border-b-0 mb-4 md:mb-0 md:pt-6"
    >
      {/* Mobile: horizontal scroll strip under the page title. */}
      <ul className="md:hidden flex flex-row gap-1.5 overflow-x-auto pb-3">
        {ADMIN_NAV.map((item) => (
          <li key={item.href} className="shrink-0">
            {navLink(item)}
          </li>
        ))}
      </ul>

      {/* Desktop: grouped sub-rail with mono eyebrows. */}
      <ul className="hidden md:flex md:flex-col md:gap-0.5">
        {ADMIN_NAV.map((item, idx) => {
          const showGroup = item.group !== lastGroup;
          lastGroup = item.group;
          return (
            <li key={item.href}>
              {showGroup ? (
                <p
                  className={
                    "font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)] px-3 pb-1.5 " +
                    (idx === 0 ? "" : "pt-4")
                  }
                >
                  {t(GROUP_LABEL_KEY[item.group])}
                </p>
              ) : null}
              {navLink(item)}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
